/**
 * @file    FiltersSettingsManager.tsx
 * @scope   VIEW
 * @summary Per-field advanced filter editor: add/edit/remove a field's filters (field → operator →
 *          values) combined with an AND/OR operator.
 * @consumes FieldsManager models, filterTypeResolver, operator options, useFilterStates
 * @produces `FiltersSettingsManager` component + its Tab descriptor
 * @sideEffects None directly — edits bubble up via callbacks/context.
 * @notes Sibling of PartitionFiltersManager (partition-level filters); shares the same widgets and
 *        the MAX_FILTERS cap. Reached from a field card's Filters tab via `onEditFilter`.
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import Select from 'react-select';
import { Icon } from '@cx/ui';
import { Tab } from 'components/TabNavigator/TabNavigator';
import {
  SelectedFieldModel,
  FieldFilter,
  FilterLogicalOperator,
} from 'models/Enterprise/FieldsManagerModel';
import { AdvancedFilterFieldConfig } from 'models/WidgetModel';
import MultipleSelectSortable, {
  SelectOptions,
} from 'core-ui/MultipleSelectSortable/MultipleSelectSortable';
import { useFilterStates } from 'hooks/Enterprise/useFilterState';
import { FilterOperatorOption, getOperatorsForField } from '../FieldsManager/mockFiltersOptions';
import { MAX_FILTERS } from '../FieldsManager/FiltersTab';
import { resolveAttributePath, resolveAttributeKind } from '../../utils/filters/filterTypeResolver';
import { resolveResponseOptions } from '../../utils/filters/booleanResponseOptions';
import { mapResponseToFilterOption } from '../../utils/filters/responseFilterOptions';
import './FiltersSettingsManager.scss';

/** Returns true when the field type should render a numeric text input instead of a dropdown. */
const isNumericField = (subAttribute: string): boolean => subAttribute === 'Numeric Value';
/** Returns true when the field type should render a text input instead of a dropdown. */
const isTextField = (subAttribute: string): boolean => subAttribute === 'Text';

export const FiltersSettingsManagerTab: Tab = {
  name: 'FiltersSettingsManager',
  displayName: 'Filters',
};

/** Custom styles for react-select dropdowns within the filters section */
const customSelectStyles = {
  control: (styles: any) => ({
    ...styles,
    maxWidth: '100%',
  }),
};

interface FiltersSettingsManagerProps {
  onBack: () => void;
  selectedField: SelectedFieldModel | null;
  onFieldChange?: (field: SelectedFieldModel) => void;
  /** If set, scroll to this filter on mount */
  focusedFilterId?: string | null;
  /** Available filter fields from Config.Filters.AdvancedFilters */
  availableFilterFields: AdvancedFilterFieldConfig[];
}

/**
 * FiltersSettingsManager — Full-page filter editing view.
 *
 * Equivalent to NpsSettingsManager / CalculationsSettingsManager
 * but for configuring advanced filters on a field.
 */
const FiltersSettingsManager: React.FC<FiltersSettingsManagerProps> = ({
  selectedField,
  onFieldChange,
  focusedFilterId,
  availableFilterFields,
}) => {
  if (!selectedField) return null;

  const filters: FieldFilter[] = selectedField.filters ?? [];
  const [highlightedFilterId, setHighlightedFilterId] = useState<string | null>(null);

  /** Refs for scroll-to-filter functionality */
  const filterRowRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  /** Scroll to focusedFilterId on mount or to newly added filters */
  useEffect(() => {
    const filterIdToScroll = highlightedFilterId ?? focusedFilterId;
    if (filterIdToScroll) {
      let timeout: ReturnType<typeof setTimeout> | undefined;
      const frame = requestAnimationFrame(() => {
        const el = filterRowRefs.current.get(filterIdToScroll);
        if (!el) return;

        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('filters-settings__filter-row--highlighted');
        timeout = setTimeout(() => {
          el.classList.remove('filters-settings__filter-row--highlighted');
          if (highlightedFilterId === filterIdToScroll) setHighlightedFilterId(null);
        }, 1500);
      });
      return () => {
        cancelAnimationFrame(frame);
        if (timeout) clearTimeout(timeout);
      };
    }
  }, [focusedFilterId, highlightedFilterId, filters.length]);

  const filterStates = useFilterStates(filters);
  const canAddMore = filters.length < MAX_FILTERS;

  const availableFieldOptions = availableFilterFields.map((f) => ({
    value: f.attributeGuid,
    label: f.displayName || f.shortText || f.columnName,
  }));

  const findField = useCallback(
    (guid: string) => availableFilterFields.find((f) => f.attributeGuid === guid),
    [availableFilterFields]
  );

  const getResponsesForField = useCallback(
    (fieldGuid: string): SelectOptions[] => {
      const field = findField(fieldGuid);
      if (!field) return [];
      return field.responses.map((r) => mapResponseToFilterOption(r, field.typeName));
    },
    [findField]
  );

  const getFieldOperators = useCallback(
    (fieldGuid: string): FilterOperatorOption[] => getOperatorsForField(findField(fieldGuid)),
    [findField]
  );

  /** Persist updated filters array to the parent */
  const updateFilters = useCallback(
    (newFilters: FieldFilter[]) => {
      if (!selectedField || !onFieldChange) return;
      onFieldChange({
        ...selectedField,
        filters: newFilters,
      });
    },
    [selectedField, onFieldChange]
  );

  const handleAddFilter = () => {
    if (!canAddMore) return;
    const newFilterId = crypto.randomUUID();
    const newFilter: FieldFilter = {
      id: newFilterId,
      filterGuid: '',
      filterName: '',
      fieldTypeName: '',
      attributePath: '',
      attributeKind: 'Scalar',
      subAttribute: '',
      operator: '',
      selectedValues: [],
      selectedValueGuid: '',
      selectedValueLabel: '',
      logicalOperator: 'AND',
    };
    setHighlightedFilterId(newFilterId);
    updateFilters([...filters, newFilter]);
  };

  const handleRemoveFilter = (filterId: string) => {
    updateFilters(filters.filter((f) => f.id !== filterId));
  };

  // Path/kind resolution is centralised in filterTypeResolver so serializer,
  // recovery and this UI stay in sync when new TypeName values are added.
  const handleFieldChange = (filterId: string, option: any) => {
    if (!option) return;
    const field = findField(option.value);
    const operators = getOperatorsForField(field);
    const updated = filters.map((f) =>
      f.id === filterId
        ? {
            ...f,
            filterGuid: option.value,
            filterName: option.label,
            fieldTypeName: field?.typeName || '',
            attributePath: resolveAttributePath(field),
            attributeKind: resolveAttributeKind(field),
            subAttribute: field?.subAttribute || '',
            operator: operators.length > 0 ? operators[0].value : '',
            selectedValues: [],
          }
        : f
    );
    updateFilters(updated);
  };

  const handleOperatorChange = (filterId: string, option: any) => {
    if (!option) return;
    const updated = filters.map((f) => {
      if (f.id !== filterId) return f;
      const shouldClearValues = option.value === '=' && f.selectedValues.length > 1;
      return {
        ...f,
        operator: option.value,
        ...(shouldClearValues ? { selectedValues: [] } : {}),
      };
    });
    updateFilters(updated);
  };

  const handleValuesChange = (filterId: string, values: SelectOptions[]) => {
    const updated = filters.map((f) => (f.id === filterId ? { ...f, selectedValues: values } : f));
    updateFilters(updated);
  };

  /** Sets the operator joining `filterId` to the filter immediately before it. */
  const handleFilterLogicalOperatorChange = (filterId: string, op: FilterLogicalOperator) => {
    updateFilters(filters.map((f) => (f.id === filterId ? { ...f, logicalOperator: op } : f)));
  };

  return (
    <div className="manage-fields-content filters-settings">
      <div className="data-source-info filters-settings__header">
        <span className="dataSourceLabel dataSourcePartitionLabel">
          {selectedField.displayName || 'Untitled Field'}
        </span>
        <span className="dataSourceFieldsLabel">Advanced Filters</span>
      </div>

      <div className="filters-settings__body">
        {/* ── Empty state ── */}
        {filters.length === 0 && (
          <p className="filters-settings__empty">No advanced filters configured.</p>
        )}

        {/* ── Filter rows ── */}
        {filters.map((filter, idx) => {
          const filterState = filterStates.get(idx);
          if (!filterState) return null;

          const operatorOptions = filter.filterGuid ? getFieldOperators(filter.filterGuid) : [];
          const responseOptions = resolveResponseOptions(
            filter.fieldTypeName,
            filter.filterGuid ? getResponsesForField(filter.filterGuid) : []
          );
          const selectedFieldOption = filter.filterGuid
            ? { value: filter.filterGuid, label: filter.filterName }
            : null;
          const selectedOperator = filter.operator
            ? operatorOptions.find((o) => o.value === filter.operator) ?? null
            : null;

          return (
            <React.Fragment key={filter.id}>
              {/* AND/OR toggle between this filter and the one before it */}
              {idx > 0 && (
                <div className="filters-settings__logical-operator">
                  <button
                    type="button"
                    className={`filters-settings__operator-btn${
                      (filter.logicalOperator ?? 'AND') === 'AND'
                        ? ' filters-settings__operator-btn--active'
                        : ''
                    }`}
                    onClick={() => handleFilterLogicalOperatorChange(filter.id, 'AND')}
                  >
                    AND
                  </button>
                  <button
                    type="button"
                    className={`filters-settings__operator-btn${
                      filter.logicalOperator === 'OR'
                        ? ' filters-settings__operator-btn--active'
                        : ''
                    }`}
                    onClick={() => handleFilterLogicalOperatorChange(filter.id, 'OR')}
                  >
                    OR
                  </button>
                </div>
              )}

              <div
                className="filters-settings__filter-row"
                ref={(el) => {
                  if (el) filterRowRefs.current.set(filter.id, el);
                  else filterRowRefs.current.delete(filter.id);
                }}
              >
                {/* 1. Field/Question selector */}
                {filterState.FieldSelect.visible && (
                  <div className="filters-settings__field-select">
                    <div className="diy-input-container">
                      <div className="diy-material-textfield">
                        <Select
                          className="diy-select"
                          options={availableFieldOptions}
                          value={selectedFieldOption}
                          onChange={(opt) => handleFieldChange(filter.id, opt)}
                          styles={customSelectStyles}
                          placeholder="Select a field..."
                          isDisabled={filterState.FieldSelect.disabled}
                        />
                        <label
                          className={`diy-label${
                            filterState.FieldSelect.disabled ? ' field-label-disabled' : ''
                          }`}
                        >
                          Field
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {/* Delete icon */}
                {filterState.RemoveButton.visible && (
                  <div
                    className="diy-drawer-icon filters-settings__delete-icon"
                    onClick={() => handleRemoveFilter(filter.id)}
                    title="Remove filter"
                  >
                    <Icon name="system-delete" />
                  </div>
                )}

                {/* 2. Operator selector */}
                {filterState.Operator.visible && (
                  <div className="filters-settings__operator-select">
                    <div className="diy-input-container">
                      <div className="diy-material-textfield">
                        <Select
                          className="diy-select"
                          options={operatorOptions}
                          value={selectedOperator}
                          onChange={(opt) => handleOperatorChange(filter.id, opt)}
                          styles={customSelectStyles}
                          placeholder="Select operator..."
                          isDisabled={filterState.Operator.disabled}
                        />
                        <label
                          className={`diy-label${
                            filterState.Operator.disabled ? ' field-label-disabled' : ''
                          }`}
                        >
                          Condition
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {/* 3. Value selector — adapts based on field type and operator */}
                {filterState.Values.visible && (
                  <div className="filters-settings__values-select">
                    {isNumericField(filter.subAttribute) ? (
                      /* Numeric fields: free-text input restricted to numbers */
                      <div className="diy-input-container">
                        <div className="diy-material-textfield">
                          <input
                            type="number"
                            className="diy-input"
                            placeholder="Enter a numeric value"
                            value={String(filter.selectedValues[0]?.value ?? '')}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === '') {
                                handleValuesChange(filter.id, []);
                              } else {
                                handleValuesChange(filter.id, [
                                  { value: Number(val), label: val, longText: val, raw: null },
                                ]);
                              }
                            }}
                          />
                          <label className="diy-label">Value</label>
                        </div>
                      </div>
                    ) : isTextField(filter.subAttribute) ? (
                      /* Text fields: free-text input */
                      <div className="diy-input-container">
                        <div className="diy-material-textfield">
                          <input
                            type="text"
                            className="diy-input"
                            placeholder="Enter a text value"
                            value={String(filter.selectedValues[0]?.value ?? '')}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === '') {
                                handleValuesChange(filter.id, []);
                              } else {
                                handleValuesChange(filter.id, [
                                  { value: val, label: val, longText: val, raw: null },
                                ]);
                              }
                            }}
                          />
                          <label className="diy-label">Value</label>
                        </div>
                      </div>
                    ) : filter.operator === '=' ? (
                      /* "Is Exactly" operator: single-value dropdown */
                      <div className="diy-input-container">
                        <div className="diy-material-textfield">
                          <Select
                            className="diy-select"
                            options={responseOptions}
                            value={
                              filter.selectedValues.length > 0
                                ? {
                                    value: filter.selectedValues[0].value,
                                    label: filter.selectedValues[0].label,
                                  }
                                : null
                            }
                            onChange={(opt: any) => {
                              if (!opt) {
                                handleValuesChange(filter.id, []);
                              } else {
                                const match = responseOptions.find((r) => r.value === opt.value);
                                handleValuesChange(filter.id, match ? [match] : []);
                              }
                            }}
                            styles={customSelectStyles}
                            placeholder="Select a value..."
                            isClearable
                          />
                          <label className="diy-label">Value</label>
                        </div>
                      </div>
                    ) : (
                      /* Default: multi-select for values */
                      <MultipleSelectSortable
                        loading={false}
                        options={responseOptions}
                        selectedValues={filter.selectedValues}
                        selectedChange={(values: SelectOptions[]) =>
                          handleValuesChange(filter.id, values || [])
                        }
                        // eslint-disable-next-line @typescript-eslint/no-empty-function
                        handleDragEnd={() => {}}
                        label="Values"
                        // eslint-disable-next-line @typescript-eslint/no-empty-function
                        onBlurEvent={() => {}}
                        helperText="Select one or more values"
                      />
                    )}
                  </div>
                )}
              </div>
            </React.Fragment>
          );
        })}

        {/* "Add Advanced Filter" link */}
        {canAddMore && (
          <div className="filters-settings__add-container">
            <a
              href="#"
              className="manage-fields-link filters-settings__add"
              onClick={(e) => {
                e.preventDefault();
                handleAddFilter();
              }}
            >
              Add Advanced Filter ({filters.length}/{MAX_FILTERS})
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default FiltersSettingsManager;
